import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const loginPlaceId = localStorage.getItem("placeId");

const NIC_COLUMN_WIDTH = 14;
const CUSTOMER_NO_HEADER = "Customer No";
const MIN_COLUMN_WIDTH = 18;
const COLUMN_WIDTH_PADDING = 4;

const fitCustomerNoWidth = (rows) => {
  let longest = 0;
  (rows || []).forEach((customer) => {
    if (customer && customer.cusNo !== null && customer.cusNo !== undefined) {
      longest = Math.max(longest, String(customer.cusNo).trim().length);
    }
  });
  return Math.max(longest, CUSTOMER_NO_HEADER.length) + 1;
};

const TABLE_HEADER_ROW = 8;

const pad = (n) => String(n).padStart(2, "0");
const generatedAt = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const addReportHeader = (worksheet, title, filters) => {
  const lines = [
    [title, { font: { bold: true, size: 16, color: { argb: "FF2F75B5" } } }],
    [`SELECTED BRANCHES: ${filters.branches || "All"}`, { font: { bold: true, size: 11 } }],
    [`SELECTED WASAMS: ${filters.wasams || "All"}`, { font: { size: 11 } }],
    [`DEPOSIT PRODUCTS: ${filters.depositProducts || "All"}`, { font: { size: 11 } }],
    [
      filters.loanProducts ? `LOAN PRODUCTS: ${filters.loanProducts}` : "",
      { font: { size: 11 } },
    ],
    [`Report Generated: ${generatedAt()}`, { font: { italic: true, size: 10 } }],
  ];

  lines.forEach(([text, style], i) => {
    const cell = worksheet.getCell(i + 1, 1);
    cell.value = text;
    cell.font = style.font;
    cell.alignment = { horizontal: "left", vertical: "middle" };
  });
};

const setTableHeaderRow = (worksheet, headers) => {
  const headerRow = worksheet.getRow(TABLE_HEADER_ROW);
  headers.forEach((header, i) => {
    headerRow.getCell(i + 1).value = header;
  });
  return headerRow;
};

const getTextWidth = (value) => {
  if (value === null || value === undefined) return 0;
  const text = value.toString().trim();
  let width = 0;
  for (let i = 0; i < text.length; i++) {
    width += text.charCodeAt(i) > 255 ? 2 : 1;
  }
  return width;
};

export const generateCustomerDetailsExcel = async (data, filters = {}) => {
  if (!Array.isArray(data)) {
    console.error("Invalid data for Excel generation:", data);
    return;
  }

  const nonNicCustomers = data.filter(
    (customer) =>
      customer.nicNo === null ||
      customer.nicNo === undefined ||
      String(customer.nicNo).trim() === ""
  );

  const customerNoColumnWidth = fitCustomerNoWidth(data);
  const nonNicCustomerNoColumnWidth =
    nonNicCustomers.length > 0
      ? fitCustomerNoWidth(nonNicCustomers)
      : customerNoColumnWidth;

  let nonNicFullNameMaxLength = 0;
  let nonNicAddressMaxLength = 0;
  if (nonNicCustomers.length > 0) {
    nonNicCustomers.forEach((customer) => {
      nonNicFullNameMaxLength = Math.max(
        nonNicFullNameMaxLength,
        getTextWidth(customer.fullName)
      );
      nonNicAddressMaxLength = Math.max(
        nonNicAddressMaxLength,
        getTextWidth(customer.address)
      );
    });
  } else {
    data.forEach((customer) => {
      nonNicFullNameMaxLength = Math.max(
        nonNicFullNameMaxLength,
        getTextWidth(customer.fullName)
      );
      nonNicAddressMaxLength = Math.max(
        nonNicAddressMaxLength,
        getTextWidth(customer.address)
      );
    });
    nonNicFullNameMaxLength = Math.min(nonNicFullNameMaxLength, 37);
    nonNicAddressMaxLength = Math.min(nonNicAddressMaxLength, 40);
  }
  const fullNameColumnWidth =
    nonNicFullNameMaxLength < 30 ? 30 : nonNicFullNameMaxLength + 3;
  const addressColumnWidth =
    nonNicAddressMaxLength < 15 ? 15 : nonNicAddressMaxLength + 2;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Customer Details");

  const depositProductSet = new Set();
  const loanProductSet = new Set();

  data.forEach((customer) => {
    if (customer.depositProducts) {
      Object.keys(customer.depositProducts).forEach((prod) => depositProductSet.add(prod));
    }
    if (customer.loanProducts) {
      Object.keys(customer.loanProducts).forEach((prod) => loanProductSet.add(prod));
    }
  });

  const depositProducts = Array.from(depositProductSet);
  const loanProducts = Array.from(loanProductSet);

  const staticHeaders = ["Customer No", "Full Name", "NIC", "Mobile", "Address", "Date of Birth", "Gender","GnName"];
  const depositHeaders = depositProducts.map((prod) => `Deposit - ${prod}`);
  const loanHeaders = loanProducts.map((prod) => `Loan - ${prod}`);


  const headers = [
    ...staticHeaders,
    ...depositHeaders,
    "Total Deposits",
    ...loanHeaders,
    "Total Loans",
  ];

  addReportHeader(worksheet, "CUSTOMER DETAILS REPORT", filters);
  setTableHeaderRow(worksheet, headers);

  data.forEach((customer) => {
    const row = [
      customer.cusNo,
      customer.fullName,
      customer.nicNo,
      customer.mobile,
      customer.address || "",
      customer.dateofBirth || customer.dateOfBirth || "",
      customer.gender || "",
      customer.gnName || "",
    ];

    let depositCount = 0;
    depositProducts.forEach((prod) => {
      const val = customer.depositProducts?.[prod] ?? "";
      if (val !== "" && val !== 0) depositCount++;
      row.push(val);
    });

    row.push(depositCount);

    let loanCount = 0;
    loanProducts.forEach((prod) => {
      const val = customer.loanProducts?.[prod] ?? "";
      if (val !== "" && val !== 0) loanCount++;
      row.push(val);
    });

    row.push(loanCount);

    const newRow = worksheet.addRow(row);

  
    if (newRow.number % 2 === 0) {
      newRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD9D9D9" },
        };
      });
    }
  });

  // Auto column width
  worksheet.columns.forEach((col, index) => {
    let maxLength = 0;
    col.eachCell({ includeEmpty: true }, (cell) => {
      maxLength = Math.max(maxLength, getTextWidth(cell.value));
    });

    if (headers[index] === "Customer No") {
      col.width = customerNoColumnWidth;
    } else if (headers[index] === "NIC") {
      col.width = NIC_COLUMN_WIDTH;
    } else if (headers[index] === "Full Name") {
      col.width = fullNameColumnWidth;
    } else if (headers[index] === "Address") {
      col.width = addressColumnWidth;
    } else {
      col.width = maxLength < MIN_COLUMN_WIDTH ? MIN_COLUMN_WIDTH : maxLength + COLUMN_WIDTH_PADDING;
    }
  });

  const headerRow = worksheet.getRow(TABLE_HEADER_ROW);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };

  if (loginPlaceId === "1") {
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.protection = { locked: true };
      });
    });

    await worksheet.protect("readonly123", {
      selectLockedCells: true,
      selectUnlockedCells: true,
      formatCells: false,
      formatColumns: false,
      formatRows: false,
      insertColumns: false,
      insertRows: false,
      insertHyperlinks: false,
      deleteColumns: false,
      deleteRows: false,
    });
  } else {
  
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.protection = { locked: false };
      });
    });
  }

  worksheet.views = [{ state: "frozen", xSplit: 3, ySplit: TABLE_HEADER_ROW }];

  // ===== Sheet 2 : customers without a NIC (added on top of the existing report) =====
  const nonNicWorksheet = workbook.addWorksheet("Non NIC Customers");

  addReportHeader(nonNicWorksheet, "NON NIC CUSTOMERS", filters);
  setTableHeaderRow(nonNicWorksheet, headers);

  nonNicCustomers.forEach((customer) => {
    const nonNicRow = [
      customer.cusNo,
      customer.fullName,
      customer.nicNo,
      customer.mobile,
      customer.address || "",
      customer.dateofBirth || customer.dateOfBirth || "",
      customer.gender || "",
      customer.gnName || "",
    ];

    let nonNicDepositCount = 0;
    depositProducts.forEach((prod) => {
      const val = customer.depositProducts?.[prod] ?? "";
      if (val !== "" && val !== 0) nonNicDepositCount++;
      nonNicRow.push(val);
    });

    nonNicRow.push(nonNicDepositCount);

    let nonNicLoanCount = 0;
    loanProducts.forEach((prod) => {
      const val = customer.loanProducts?.[prod] ?? "";
      if (val !== "" && val !== 0) nonNicLoanCount++;
      nonNicRow.push(val);
    });

    nonNicRow.push(nonNicLoanCount);

    const nonNicNewRow = nonNicWorksheet.addRow(nonNicRow);

    if (nonNicNewRow.number % 2 === 0) {
      nonNicNewRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD9D9D9" },
        };
      });
    }
  });

  // Auto column width
  nonNicWorksheet.columns.forEach((col, index) => {
    let maxLength = 0;
    col.eachCell({ includeEmpty: true }, (cell) => {
      maxLength = Math.max(maxLength, getTextWidth(cell.value));
    });

    if (headers[index] === "Customer No") {
      col.width = nonNicCustomerNoColumnWidth;
    } else if (headers[index] === "NIC") {
      col.width = NIC_COLUMN_WIDTH;
    } else if (headers[index] === "Full Name") {
      col.width = fullNameColumnWidth;
    } else if (headers[index] === "Address") {
      col.width = addressColumnWidth;
    } else {
      col.width =
        maxLength < MIN_COLUMN_WIDTH ? MIN_COLUMN_WIDTH : maxLength + COLUMN_WIDTH_PADDING;
    }
  });

  const nonNicHeaderRow = nonNicWorksheet.getRow(TABLE_HEADER_ROW);
  nonNicHeaderRow.font = { bold: true };
  nonNicHeaderRow.alignment = { vertical: "middle", horizontal: "center" };

  if (loginPlaceId === "1") {
    nonNicWorksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.protection = { locked: true };
      });
    });

    await nonNicWorksheet.protect("readonly123", {
      selectLockedCells: true,
      selectUnlockedCells: true,
      formatCells: false,
      formatColumns: false,
      formatRows: false,
      insertColumns: false,
      insertRows: false,
      insertHyperlinks: false,
      deleteColumns: false,
      deleteRows: false,
      autoFilter: false,
      sort: false,
    });
  } else {

    nonNicWorksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.protection = { locked: false };
      });
    });
  }

  nonNicWorksheet.views = [{ state: "frozen", xSplit: 3, ySplit: TABLE_HEADER_ROW }];

  // Filter only on the NIC_No column
  nonNicWorksheet.autoFilter = {
    from: { row: TABLE_HEADER_ROW, column: 3 },
    to: { row: nonNicWorksheet.rowCount, column: 3 },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "CustomerDetailsReport.xlsx");
};
